'use client';
import React from 'react';

import { Typography } from '@mui/material';
import { useMediaQuery, Theme } from '@mui/material';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';

import { useOsDetection } from '@/hooks/useOsDetection';
import Button from '@/ui/components/Button';
import DocumentSeoHeader from '@/ui/components/DocumentSeoHeader';
import OutsideBox from '@/ui/containers/layout/OutsideBox';
import { replaceLocalUrl } from '@/utils/helpers';
import { urlBuilder } from '@/utils/urlBuilder';
import { addLocalePath } from '@/utils/urlBuilder/utils';

export const Headline = styled.div`
  padding-bottom: 40px;
  padding-top: 20px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding-top: 1rem;
    padding-bottom: 2rem;
  }
`;

const ProductHeader = styled.div`
  align-items: center;
  display: flex;

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-flow: column;
  }

  img {
    align-self: flex-start;
    max-height: 232px;
    max-width: 427px;
    mix-blend-mode: multiply;

    ${({ theme }) => theme.breakpoints.down('md')} {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

const ProductHeaderBoxs = styled.div<{ $gap?: boolean }>`
  display: flex;
  flex-grow: 1;
  margin-left: 100px;
  ${props => !props.$gap && 'justify-content: space-between;'}
  ${props => props.$gap && 'gap: 160px;'}

  ${({ theme }) => theme.breakpoints.down('lg')} {
    margin-left: 50px;
    padding-right: 50px;
  }

  ${({ theme }) => theme.breakpoints.down(1050)} {
    padding-right: 0;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    margin-left: 0;
    margin-top: 20px;
    gap: 60px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: block;
    width: 100%;
    gap: 0;
  }
`;

const ProductHeaderBox = styled.div`
  max-width: 210px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    max-width: none;
  }

  p {
    color: #6f6f6f;
    margin-bottom: 15px;
    margin-top: 6px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin-top: 40px;
  }
`;

const ProductHeaderLinks = styled.div`
  div + div {
    margin-top: 20px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    display: flex;
    gap: 20px;

    div + div {
      margin-top: 0;
    }
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    flex-wrap: wrap;
  }
`;

const ButtonWrap = styled.div`
  display: flex;
  flex-wrap: nowrap;

  button {
    border-left-width: 0px;
    border-radius: 0;
  }

  a:first-child {
    button {
      border-left-width: 1px;
      border-top-left-radius: 2px;
      border-bottom-left-radius: 2px;
    }
  }

  a:last-child {
    button {
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
    }
  }
`;

const getDownloadButtonLabel = string => {
  switch (string) {
    case 'mac':
      return 'Apple';
    case 'win':
      return 'Windows';
    case 'linux':
      return 'Linux';
    default:
      return undefined;
  }
};

const getDownloadButtonIcon = string => {
  switch (string) {
    case 'mac':
      return 'apple';
    case 'win':
      return 'windows';
    case 'linux':
      return 'linux';
    default:
      return undefined;
  }
};

const createDescription = html => {
  // Remove HTML tags
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Get first 10 words
  const words = text.split(' ').slice(0, 10).join(' ');

  // Apply 150 character limit
  return text.length > 150 ? words.slice(0, 147) + '...' : words;
};

interface ProductLink {
  buttonLabel?: string;
  description: string;
  meta?: {
    type: {
      value: string;
      label: string;
    };
    release_date: string;
    files: {
      platform: string;
      show_linux_info: boolean;
      file_url: string;
      file_size: string;
    }[];
    release_log_url?: string;
  };
  slug: string;
  title: string;
  type: string;
}

interface GrayStripProps {
  product: Product;
  links: ProductLink[] | Product[];
  locale: string;
}
const getProductThumbnail = media => {
  const sizePriority = [
    'thumbnail',
    'medium',
    'medium_large',
    'large',
    '1536x1536',
    'original',
  ];

  const smallestAvailable = sizePriority.find(size => media[size]);
  return smallestAvailable ? replaceLocalUrl(media[smallestAvailable]) : null;
};

export default function GrayStrip({ product, links, locale }: GrayStripProps) {
  const isMdDown = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );

  const translate = useTranslations();
  const detectedOs = useOsDetection('win');

  const supportTitle = translate('breadcrumb-support').toLowerCase();

  const productThumbnail = getProductThumbnail(product.media.image);

  return (
    <OutsideBox>
      <DocumentSeoHeader
        title={product.content}
        description={product.description}
        image={productThumbnail}
      />
      <ProductHeader>
        {productThumbnail && (
          <img
            src={productThumbnail}
            alt={`${product.content} ${supportTitle}`}
          />
        )}

        <ProductHeaderBoxs $gap={links.length === 2}>
          {links.slice(0, 3).map(link => (
            <ProductHeaderBox key={link.slug}>
              <Typography variant="h4" component="h2">
                {link.title.replace(
                  'Handbook',
                  translate('page-product-handbook')
                )}
              </Typography>

              {link.type === 'useful_links' ? (
                <p></p>
              ) : link?.description ? (
                <p>{link.description}</p>
              ) : (
                link?.content && <p>{createDescription(link.content)}</p>
              )}

              <ProductHeaderLinks>
                <>
                  {link.type == 'prusa3d_downloads' ? (
                    <>
                      <div>
                        {link?.meta?.files?.find(
                          file => file.platform === 'all'
                        ) ? (
                          <>
                            <Button
                              label={translate('page-product-button-download')}
                              size="large"
                              variant={isMdDown ? 'outlined' : undefined}
                              href={
                                link.meta.files.find(
                                  file => file.platform === 'all'
                                )?.file_url
                              }
                            />
                          </>
                        ) : (
                          <ButtonWrap>
                            <Button
                              label={getDownloadButtonLabel(detectedOs)}
                              size="large"
                              variant={isMdDown ? 'outlined' : undefined}
                              icon={getDownloadButtonIcon(detectedOs)}
                              href={
                                link?.meta?.files?.find(
                                  file => file.platform === detectedOs
                                )?.file_url
                              }
                            />
                            {/* {link?.meta?.files
                                .filter(file => file.platform !== detectedOs)
                                .map(button => (
                                  <Button
                                    key={button.platform}
                                    size="large"
                                    icon={getDownloadButtonIcon(
                                      button.platform
                                    )}
                                    href={button.file_url}
                                  />
                                ))} */}
                          </ButtonWrap>
                        )}
                      </div>
                      <div>
                        {link?.meta?.type?.value === 'firmware' && (
                          <>
                            <Button
                              label={translate('page-product-button-changelog')}
                              variant="outlined"
                              href={addLocalePath(
                                locale,
                                `/changelog/${product.slug}`
                              )}
                            />
                          </>
                        )}
                        {link.title.toLowerCase().includes('prusaslicer') && (
                          <>
                            <Button
                              label={translate('page-product-button-changelog')}
                              variant="outlined"
                              href={`https://files.prusa3d.com/?latest=slicer-stable&lng=${locale}`}
                            />
                          </>
                        )}
                      </div>
                    </>
                  ) : link.type == 'helps' ? (
                    <div>
                      <Button
                        label={`${link?.buttonLabel ||
                          translate('page-product-button-article')
                          }`}
                        variant="outlined"
                        href={urlBuilder({
                          item: link,
                          locale,
                        })}
                      />
                    </div>
                  ) : link.type == 'directly_to_downloads' ? (
                    <div>
                      <Button
                        label={translate(
                          'page-product-button-firmware-bundles'
                        )}
                        size="large"
                        href={addLocalePath(locale, link.slug)}
                      />
                    </div>
                  ) : link.type == 'useful_links' ? (
                    <div>
                      <Button
                        label={translate('page-product-button-link')}
                        variant="outlined"
                        href={link.content.replace(/<[^>]*>/g, '').trim()}
                      />
                    </div>
                  ) : (
                    <div>
                      {link?.meta?.type?.value === 'firmware' && (
                        <Button
                          label={`${link?.buttonLabel ||
                            translate('page-product-button-changelog')
                            }`}
                          variant="outlined"
                          href={`${link.slug}`}
                        />
                      )}
                    </div>
                  )}
                </>
              </ProductHeaderLinks>
            </ProductHeaderBox>
          ))}
        </ProductHeaderBoxs>
      </ProductHeader>
    </OutsideBox>
  );
}
